/**
 * Reports Repository Implementation
 *
 * Repository implementation for the Reports feature
 */

import {ReportsAPIDataSource} from '../api/ReportsAPIDataSource';
import {ReportsRepository} from './ReportsRepository';
import {
  Marketer,
  MarketerStoresReport,
  MarketerFinancialReport,
  MarketerStockReport,
  ExportResponse,
  ReportFilters,
  ExportRequest
} from '../models/report.model';
import {reportMapper} from '../mappers/report.mapper';

export class ReportsRepositoryImpl implements ReportsRepository {
  constructor (private apiDataSource: ReportsAPIDataSource) {}

  async getMarketers (): Promise<Marketer[]> {
    try {
      const response = await this.apiDataSource.getMarketers();
      return response.data.map(marketer => reportMapper.toMarketer(marketer));
    } catch (error) {
      throw new Error(`Failed to fetch marketers: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getMarketerStoresReport (marketerId: number, filters?: ReportFilters): Promise<MarketerStoresReport> {
    try {
      const response = await this.apiDataSource.getMarketerStoresReport(marketerId, filters);
      return reportMapper.toMarketerStoresReport(response.data);
    } catch (error) {
      throw new Error(`Failed to fetch marketer stores report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getMarketerFinancialReport (marketerId: number, filters?: ReportFilters): Promise<MarketerFinancialReport> {
    try {
      const response = await this.apiDataSource.getMarketerFinancialReport(marketerId, filters);
      return reportMapper.toMarketerFinancialReport(response.data);
    } catch (error) {
      throw new Error(`Failed to fetch marketer financial report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getMarketerStockReport (marketerId: number, filters?: ReportFilters): Promise<MarketerStockReport> {
    try {
      const response = await this.apiDataSource.getMarketerStockReport(marketerId, filters);
      return reportMapper.toMarketerStockReport(response.data);
    } catch (error) {
      throw new Error(`Failed to fetch marketer stock report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async exportMarketerReport (marketerId: number, request: ExportRequest): Promise<ExportResponse | MarketerStoresReport> {
    try {
      const response = await this.apiDataSource.exportMarketerReport(marketerId, request);

      // If format is JSON, return the data directly
      if (request.format === 'json') {
        return reportMapper.toMarketerStoresReport(response.data as any);
      }

      // For Excel/PDF, return export response
      return reportMapper.toExportResponse(response.data as any);
    } catch (error) {
      throw new Error(`Failed to export marketer report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}


